import React from 'react'
import AgTechTourHelpButton from '@agtech/web/components/App/Tour/AgTechTourHelpButton';
import { TaskTemplateDialogTourConfig } from './TaskTemplateDialogTourConfig';
import { useAgTechQuery, useAgTechQueryResultCombination } from '@agtech/core/data/queries/AgTechDataQueries';
import { AgTechFormContext } from '@agtech/core/forms/AgTechFormContext';
import { AgTechButtonProps, AgTechButton, AgTechClickEventHandler } from '@agtech/web/components/Buttons/AgTechButtons';
import { AgTechFormTextAreaControl } from '@agtech/web/components/Forms/Controls/AgTechFormTextAreaControl';
import { AgTechFormTextControl } from '@agtech/web/components/Forms/Controls/AgTechFormTextControl';
import AgTechFormBody from '@agtech/web/components/Forms/Layout/AgTechFormBody';
import { AgTechFormRow } from '@agtech/web/components/Forms/Layout/AgTechFormRow';
import { AgTechFormSection } from '@agtech/web/components/Forms/Layout/AgTechFormSection';
import AgTechFormDialog from '@agtech/web/components/Portals/Dialogs/AgTechFormDialog';
import { DefaultSaleTaskTemplate, SaleTaskTemplateItem, TaskTemplateFormData } from '@sales/data/taskTemplates/TaskTemplateData';
import { useTaskTemplateWriteWithItemsAction } from '@sales/data/taskTemplates/TaskTemplateDataActions';
import { TaskTemplateDataGetTemplatesQuery, TaskTemplateDataGetItemsQuery } from '@sales/data/taskTemplates/TaskTemplateQueries';
import TaskTemplateItemDialog from './TaskTemplateItemDialog';

export type TaskTemplateDialogProps = {
    templateId?: number,
    onTaskTemplateSubmitted?: (submittedTemplate: any) => Promise<void>
}

// Items are pure local state on the form entity here - add/edit/remove never touch the server (see
// TaskTemplateItemDialog). Editing/removing an existing item is targeted by its position in the
// array rather than its id, since newly-added items don't have a real id yet (id: 0) and can't be
// told apart from each other that way.
const TaskTemplateItemsSection = (props: { formContext: AgTechFormContext<TaskTemplateFormData, any> }) => {
    const items = props.formContext.entity.items;

    const replaceItemAt = (index: number, updated: SaleTaskTemplateItem) => {
        props.formContext.updateFormEntity(current => ({
            ...current,
            items: current.items.map((item, i) => i === index ? updated : item)
        }));
    };

    const addItem = (item: SaleTaskTemplateItem) => {
        props.formContext.updateFormEntity(current => ({
            ...current,
            items: [...current.items, item]
        }));
    };

    const removeItemAt = (index: number) => {
        props.formContext.updateFormEntity(current => {
            const item = current.items[index];

            return {
                ...current,
                items: current.items.filter((_, i) => i !== index),
                deletedItemIds: item.id ? [...current.deletedItemIds, item.id] : current.deletedItemIds
            };
        });
    };

    return (
        <AgTechFormSection header='Items' bodyClasses='wide row-based gap-y-2 flex'>
            {items.length === 0 ? (
                <div className='text-secondary'>No items have been added to this template yet.</div>
            ) : items.map((item, index) => (
                <div key={index} className='column-based items-center justify-between border rounded px-3 py-2 flex'>
                    <div className='row-based flex'>
                        <div className='font-semibold'>{item.taskName}</div>
                        <div className='text-secondary font-size-very-small'>
                            {`Starts ${item.startOffsetDays >= 0 ? '+' : ''}${item.startOffsetDays}d, ends ${item.endOffsetDays >= 0 ? '+' : ''}${item.endOffsetDays}d from sale start · Priority ${item.priority}`}
                        </div>
                    </div>
                    <div className='column-based gap-x-2 flex'>
                        <TaskTemplateItemDialog.Button
                            item={item}
                            onItemSaved={updated => replaceItemAt(index, updated)}
                            text='Edit'
                            classes='button-link'
                        />
                        <AgTechButton
                            text='Delete'
                            classes='button-link'
                            action={async () => removeItemAt(index)}
                        />
                    </div>
                </div>
            ))}
            <div>
                <TaskTemplateItemDialog.Button onItemSaved={addItem} classes='button-link' />
            </div>
        </AgTechFormSection>
    )
}

const TaskTemplateDialog = (props: TaskTemplateDialogProps) => {
    const templatesQuery = useAgTechQuery({ query: TaskTemplateDataGetTemplatesQuery });
    const itemsQuery = useAgTechQuery({
        query: TaskTemplateDataGetItemsQuery,
        queryContext: { templateId: props.templateId ?? 0 }
    });

    const dialogQuery = useAgTechQueryResultCombination({
        firstQuery: templatesQuery,
        secondQuery: itemsQuery,
        projection: (templates, items) => ({ templates, items })
    });

    const dialogAction = useTaskTemplateWriteWithItemsAction();

    return (
        <AgTechFormDialog
            width={650}
            structure={() => ({
                header: {
                    header: props.templateId ? 'Update Task Template' : 'Create Task Template',
                    settings: {
                        content: {
                            body: () => <AgTechTourHelpButton tourConfig={TaskTemplateDialogTourConfig} />
                        }
                    }
                },
                submitButtonText: props.templateId ? 'Update Template' : 'Create Template',
            })}
            dialogData={{
                query: dialogQuery,
                getDialogData: (data): TaskTemplateFormData => ({
                    template: props.templateId ? data.templates[props.templateId] : DefaultSaleTaskTemplate,
                    items: Object.values(data.items).sort((a, b) => a.startOffsetDays - b.startOffsetDays),
                    deletedItemIds: []
                })
            }}
            dialogAction={{
                action: dialogAction,
                getActionData: d => d
            }}
            dialogBehavior={() => ({
                eventHandlers: {
                    onDialogSubmitted: props.onTaskTemplateSubmitted
                }
            })}
        >
            {(formContext) => (
                <AgTechFormBody>
                    <div id='task-template-name'>
                    <AgTechFormRow>
                        <AgTechFormTextControl
                            entityType={formContext.entity}
                            caption='Name'
                            value={e => e.template.name}
                            focused={true}
                            required={true}
                            width={100}
                            onControlValueChanged={async (e, val) => ({
                                ...e,
                                template: { ...e.template, name: val }
                            })}
                        />
                    </AgTechFormRow>
                    </div>
                    <div id='task-template-description'>
                    <AgTechFormRow>
                        <AgTechFormTextAreaControl
                            entityType={formContext.entity}
                            caption='Description'
                            value={e => e.template.description}
                            rows={3}
                            width={100}
                            onControlValueChanged={async (e, val) => ({
                                ...e,
                                template: { ...e.template, description: val }
                            })}
                        />
                    </AgTechFormRow>
                    </div>
                    <div id='task-template-items'>
                    <AgTechFormRow>
                        <TaskTemplateItemsSection formContext={formContext} />
                    </AgTechFormRow>
                    </div>
                </AgTechFormBody>
            )}
        </AgTechFormDialog>
    )
}

TaskTemplateDialog.Button = (props: TaskTemplateDialogProps & AgTechButtonProps) => (
    <AgTechButton
        text={props.templateId ? 'Update Template' : 'Create Template'}
        icon={props.templateId ? 'fas fa-edit' : 'fas fa-plus'}
        {...props}
        action={e => TaskTemplateDialog.Open(e, props)}
    />
);

TaskTemplateDialog.Open = (e: AgTechClickEventHandler, props?: TaskTemplateDialogProps) => e.dialogs.openDialog({
    dialog: () => <TaskTemplateDialog templateId={props?.templateId} />
});

export default TaskTemplateDialog;
